import React from 'react';
import { Shield, Globe, Heart, Award, CheckCircle, Users, GraduationCap, Sparkles } from 'lucide-react';

export const About = () => {
  const highlights = [
    {
      icon: Award,
      title: "12+ Years Experience",
      description: "Over 12 years of expertise in pet travel compliance"
    },
    {
      icon: Globe,
      title: "30+ Countries",
      description: "Successfully facilitated pet travel to destinations worldwide"
    },
    {
      icon: Users,
      title: "Hundreds of Happy Pets",
      description: "Assisted countless furry family members reach their destinations safely"
    },
    {
      icon: Shield,
      title: "100% Compliance",
      description: "Meticulous handling of all regulatory requirements"
    }
  ];

  const whyChooseUs = [
    <>
      <span className="font-bold text-primary">Holistic approach</span> combining <span className="font-semibold text-foreground">pet health and wellness</span> with <span className="font-semibold text-foreground">regulatory compliance</span>—<span className="font-bold text-primary">veterinary-led guidance</span> that prioritizes your pet's wellbeing throughout their journey
    </>,
    <>
      <span className="font-bold text-primary">Expert consultancy</span> from <span className="font-semibold text-foreground">seasoned veterinary travel consultants</span> with over <span className="font-bold text-accent">12 years</span> of <span className="font-semibold text-foreground">proven expertise</span>
    </>,
    <>
      Member of an <span className="font-bold text-primary">international network</span> of <span className="font-bold text-accent">80+ pet travel specialists worldwide</span>, ensuring <span className="font-semibold text-foreground">expert guidance</span> for any destination at any time
    </>,
    <>
      <span className="font-bold text-primary">Comprehensive document handling</span> and <span className="font-semibold text-foreground">meticulous regulatory compliance</span> with local and international bodies
    </>,
    <>
      Navigation of <span className="font-bold text-primary">complex airline requirements</span>, <span className="font-bold text-primary">importation conditions</span>, and <span className="font-bold text-primary">customs procedures</span>
    </>,
    <>
      <span className="font-bold text-primary">Personalized service</span> <span className="font-semibold text-foreground">tailored</span> to your pet's specific health needs and travel requirements
    </>,
    <>
      <span className="font-bold text-primary">Full support</span> for <span className="font-semibold text-foreground">pet relocation</span>, <span className="font-semibold text-foreground">imports for new family members</span>, and <span className="font-semibold text-foreground">vacation travel assistance</span>
    </>
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            {/* Left: Story */}
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
                <Heart className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">About Pawsome Ventures</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Your Pet's Journey, Our Expertise
              </h2>
              
              <div className="space-y-4 text-sm sm:text-base text-foreground/70 leading-relaxed">
                <p>
                  <span className="font-semibold text-foreground">Based in the Cayman Islands</span>, Pawsome Ventures provides expert pet travel services with over 12 years of experience in pet travel compliance. We guarantee stress-free travel experiences for both you and your furry family members.
                </p>
                
                <p>
                  Led by <span className="font-semibold text-foreground">Dr. Samantha Dorman, MSc, BVSc</span>, a daughter of the soil—born and raised in the Cayman Islands—Pawsome Ventures brings over 12 years of specialized experience in pet relocation and veterinary travel consultation.
                </p>
                
                <p>
                  Dr. Samantha and her team deliver <span className="font-semibold text-primary">CaymanKind hospitality and customer service</span>, aiming to make every interaction memorable and personalized. Whether you're relocating to or from the islands, we treat your pets like family and ensure their journey is as comfortable and stress-free as possible.
                </p>
                
                <p>
                  As a trusted member of a global network of 80+ dedicated pet travel agents, we navigate the complexities of international pet travel with veterinary expertise and genuine local care. We specialize in serving four key client groups:
                </p>
                
                <div className="space-y-4 mt-6">
                  {/* Client Group 1 - Teal */}
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-primary/5 border-l-4 border-primary">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <span className="font-bold text-primary text-base sm:text-lg">Relocating TO the Cayman Islands</span>
                      <p className="text-sm sm:text-base text-foreground/70 mt-1">Complete import assistance for individuals and families bringing their pets to the Islands</p>
                    </div>
                  </div>

                  {/* Client Group 2 - Navy */}
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-secondary/10 border-l-4 border-secondary">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-secondary text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <span className="font-bold text-secondary text-base sm:text-lg">Relocating FROM the Cayman Islands</span>
                      <p className="text-sm sm:text-base text-foreground/70 mt-1">Expert export services for residents moving abroad with their pets</p>
                    </div>
                  </div>

                  {/* Client Group 3 - Accent (Lavender) */}
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-accent/10 border-l-4 border-accent">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <span className="font-bold text-accent text-base sm:text-lg">Tourists & Seasonal Residents</span>
                      <p className="text-sm sm:text-base text-foreground/70 mt-1">Comprehensive travel assistance for visitors bringing their beloved companions to enjoy the Cayman Islands. We handle the complete journey—import requirements to enter Cayman and export requirements for your return home, whether for short vacations or extended winter getaways</p>
                    </div>
                  </div>

                  {/* Client Group 4 - Deep Teal */}
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-teal-900/10 border-l-4 border-teal-700">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-teal-700 text-white text-sm font-bold">4</span>
                    </div>
                    <div>
                      <span className="font-bold text-teal-800 text-base sm:text-lg">Cayman Islands Residents</span>
                      <p className="text-sm sm:text-base text-foreground/70 mt-1">Complete vacation travel support for residents traveling abroad with their pets. We manage the full trip—export documentation to leave Cayman and import requirements for your destination country, including multiple destinations and complex itineraries. We also provide comprehensive import services when you're welcoming new furry family members to your Cayman home</p>
                    </div>
                  </div>
                </div>
                
                <p>
                  Dr. Samantha's background as a former Senior Veterinarian for the Cayman Islands and previous employee of the Cayman Islands Department of Agriculture brings invaluable in-depth knowledge of Cayman regulations and international pet travel requirements to every journey we facilitate.
                </p>
                
                <p>
                  We meticulously handle all necessary documents, ensuring compliance with local and international regulatory bodies for each jurisdiction. From navigating airline requirements, to stringent importation conditions, to complex customs procedures—we've got all aspects of your pet's travel needs covered.
                </p>
              </div>
            </div>

            {/* Right: Photo */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_pet-relocation-hub/artifacts/8xwyyq0y_ChatGPT%20Image%20Mar%2012%2C%202026%2C%2007_09_29%20AM.png"
                  alt="Dr. Samantha Dorman"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Photo Caption */}
              <div className="mt-6 text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-1">
                  Dr. Samantha Dorman
                </h3>
                <p className="text-base sm:text-lg text-primary font-semibold mb-1">
                  MSc, BVSc
                </p>
                <p className="text-sm sm:text-base text-foreground/70">
                  Founder and Lead Veterinary Travel Consultant
                </p>
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full -z-10"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-accent/10 rounded-full -z-10"></div>
            </div>
          </div>

          {/* Origin Story Section - Enhanced */}
          <div className="mb-20 relative">
            {/* Decorative background elements */}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-accent/5 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl"></div>
            
            <div className="relative bg-gradient-to-br from-accent/10 via-white to-primary/5 rounded-3xl p-8 md:p-12 border-2 border-accent/20 shadow-2xl overflow-hidden">
              {/* Decorative pattern overlay */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary rounded-full translate-y-1/2 -translate-x-1/2"></div>
              </div>
              
              <div className="max-w-4xl mx-auto relative z-10">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  {/* Icon/Badge Section */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-xl">
                        <Heart className="w-10 h-10 text-white" />
                      </div>
                      {/* Pulse ring */}
                      <div className="absolute inset-0 rounded-2xl bg-accent/20 animate-pulse"></div>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 bg-accent/20 px-4 py-2 rounded-full mb-6">
                      <Sparkles className="w-4 h-4 text-accent" />
                      <span className="text-xs font-bold text-accent uppercase tracking-wide">Our Story</span>
                    </div>
                    
                    {/* Heading */}
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
                      Why Pawsome Ventures Exists
                    </h3>
                    
                    {/* Quote Content */}
                    <div className="space-y-4 text-sm sm:text-base text-foreground/70 leading-relaxed relative">
                      {/* Opening quote mark - Enhanced */}
                      <div className="absolute -left-4 -top-2 text-8xl text-accent/20 font-serif leading-none select-none">"</div>
                      
                      <p className="relative z-10 pl-8">
                        Throughout my veterinary career, I witnessed firsthand the overwhelming stress and anxiety pet owners experienced when navigating the complex maze of international pet travel. From managing intricate compliance requirements to coordinating logistics across multiple jurisdictions, the process often felt daunting and isolating.
                      </p>
                      <p className="pl-8">
                        I saw the heartbreak when carefully laid plans unraveled due to preventable errors—mistakes made by well-meaning clients, veterinary staff, or third-party services that resulted in costly delays, re-submissions, and emotional distress. These setbacks didn't just impact travel schedules; they affected families during already stressful life transitions.
                      </p>
                      <p className="pl-8">
                        <span className="font-semibold text-foreground text-base sm:text-lg">Pawsome Ventures was born from a deep commitment to change this experience.</span> Drawing on my passion for exceptional customer service, extensive experience managing pet travel documentation across diverse jurisdictions, and unwavering belief in the power of the human-animal bond, I set out to create a service where clients could breathe easier.
                      </p>
                      <p className="pl-8">
                        Our mission is simple yet profound: <span className="font-semibold text-foreground text-base sm:text-lg">to remove obstacles and eliminate stress</span>, allowing you to focus on what truly matters—your journey ahead with your beloved furry or feathered family members. Every consultation, every document, every detail is handled with the care and precision your companions deserve.
                      </p>
                      
                      {/* Closing quote mark */}
                      <div className="text-right">
                        <div className="inline-block relative">
                          <div className="absolute -right-4 -bottom-6 text-8xl text-accent/20 font-serif leading-none select-none">"</div>
                        </div>
                      </div>
                      
                      {/* Attribution - Enhanced */}
                      <div className="pt-6 mt-6 border-t-2 border-accent/20">
                        <div className="flex items-center gap-4 pl-8">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                            <span className="text-white font-bold text-lg">SD</span>
                          </div>
                          <div>
                            <p className="font-bold text-foreground text-base sm:text-lg">Dr. Samantha Dorman</p>
                            <p className="text-xs sm:text-sm text-foreground/60">Founder & Lead Veterinary Travel Consultant</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative Image - Vet with Pet */}
          <div className="py-12 bg-gradient-to-b from-muted/20 to-background">
            <div className="container mx-auto px-4 max-w-2xl">
              <img 
                src="/vet-holding-white-dog.jpg"
                alt="Professional pet care - veterinary professional holding a white fluffy dog"
                className="w-full h-auto rounded-2xl shadow-lg object-cover"
              />
            </div>
          </div>

          {/* Leadership Section */}
          <div className="mb-20 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 md:p-10 border border-primary/10">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                  Expert Veterinary Leadership
                </h3>
                <p className="text-sm sm:text-base text-foreground/70 leading-relaxed mb-4">
                  Pawsome Ventures is led by <span className="font-semibold text-foreground">Dr. Samantha Dorman, MSc, BVSc</span>, a veterinarian with over 12 years of specialized experience in pet travel compliance. Her unique background includes:
                </p>
                
                {/* Educational Background */}
                <div className="bg-white/50 rounded-lg p-6 mb-6 border border-primary/10">
                  <h4 className="text-base sm:text-lg font-bold text-foreground mb-4">Educational Background</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm sm:text-base font-semibold text-foreground">Master of Science (MSc) One Health</p>
                        <p className="text-xs sm:text-sm text-foreground/70">University of Edinburgh, Scotland (2026)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm sm:text-base font-semibold text-foreground">Bachelor of Veterinary Science (BVSc)</p>
                        <p className="text-xs sm:text-sm text-foreground/70">Massey University, New Zealand (2012)</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Professional Experience */}
                <h4 className="text-base sm:text-lg font-bold text-foreground mb-4">Professional Experience & Expertise</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-foreground/70">Former Senior Veterinarian for the Cayman Islands</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-foreground/70">Previous employee of the Cayman Islands Department of Agriculture</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-foreground/70">In-depth knowledge of international pet travel regulations</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-foreground/70">Expert guidance on vaccinations and pet-specific travel needs</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pawsome Ventures Logo - Before Stats */}
          <div className="flex justify-center py-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_pet-relocation-hub/artifacts/vmr2h399_Pawsome%20Logo.jpg"
              alt="Pawsome Ventures Logo"
              className="h-96 w-auto object-contain"
            />
          </div>

          {/* Highlights Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {highlights.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                    <IconComponent className="w-8 h-8 text-primary group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-foreground/60">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Why Choose Us */}
          <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 rounded-2xl p-8 md:p-12 border border-primary/10">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground text-center mb-8">
                Why Choose Pawsome Ventures?
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </div>
                    <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10 text-center">
                <p className="text-base sm:text-lg text-foreground/80 font-medium mb-4">
                  Trust in our <span className="font-bold text-primary">veterinary expertise</span> to make your pet's journey as <span className="font-semibold text-foreground">smooth and hassle-free</span> as possible.
                </p>
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                >
                  Start Your Pet's Journey Today
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
